import React, { useEffect, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Smartphone, Apple, X, Share, Plus, CheckCircle2 } from "lucide-react";

/**
 * Prenova install-app buttons. Designed to live next to the hero logo on
 * the landing page. Visual style: dark glass surface with gold accents, hover
 * raises a subtle gold halo. Two CTAs:
 *   • Android — uses the captured `beforeinstallprompt` to install in-place.
 *   • iOS Safari — opens an instructional modal.
 */
export default function InstallAppButtons({ size = "md" }) {
    const { t } = useTranslation();
    const [deferredPrompt, setDeferredPrompt] = useState(null);
    const [installed, setInstalled] = useState(false);
    const [showIOS, setShowIOS] = useState(false);

    const isIOS = useMemo(() => {
        if (typeof navigator === "undefined") return false;
        const ua = navigator.userAgent || "";
        return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    }, []);

    const isStandalone = useMemo(() => {
        if (typeof window === "undefined") return false;
        return (
            window.matchMedia?.("(display-mode: standalone)").matches ||
            window.navigator.standalone === true
        );
    }, []);

    useEffect(() => {
        const onPrompt = (e) => {
            e.preventDefault();
            setDeferredPrompt(e);
        };
        const onInstalled = () => {
            setInstalled(true);
            setDeferredPrompt(null);
        };
        window.addEventListener("beforeinstallprompt", onPrompt);
        window.addEventListener("appinstalled", onInstalled);
        return () => {
            window.removeEventListener("beforeinstallprompt", onPrompt);
            window.removeEventListener("appinstalled", onInstalled);
        };
    }, []);

    const installAndroid = async () => {
        if (!deferredPrompt) return;
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        if (outcome === "accepted") {
            setInstalled(true);
        }
        setDeferredPrompt(null);
    };

    if (isStandalone || installed) {
        return (
            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-sm border border-emerald-400/40 bg-emerald-400/10 text-emerald-300 text-[11px] tracking-[0.25em] uppercase font-semibold" data-testid="install-app-installed">
                <CheckCircle2 size={14} strokeWidth={1.6} />
                <span>{t("install.installed")}</span>
            </div>
        );
    }

    const androidDisabled = !deferredPrompt;

    // Compact / large size variants — both share the same visual language.
    const padX = size === "sm" ? "px-3.5" : "px-5";
    const padY = size === "sm" ? "py-2.5" : "py-3";
    const fontSize = size === "sm" ? "text-[10px]" : "text-[11px]";

    return (
        <>
            <div className="flex flex-col sm:flex-row gap-3 max-w-md" data-testid="install-app-buttons">
                <button
                    type="button"
                    onClick={installAndroid}
                    disabled={androidDisabled}
                    title={androidDisabled ? t("install.android_unavailable") : t("install.android_cta")}
                    data-testid="install-android-btn"
                    className={`group inline-flex items-center justify-center gap-2.5 ${padX} ${padY} rounded-sm ${fontSize} font-semibold tracking-[0.3em] uppercase transition-all duration-300 ${
                        androidDisabled
                            ? "bg-white/[0.03] border border-white/10 text-gray-600 cursor-not-allowed"
                            : "bg-gold text-black border border-gold hover:bg-gold-light hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] hover:-translate-y-0.5"
                    }`}
                >
                    <Smartphone size={14} strokeWidth={1.8} className={androidDisabled ? "" : "group-hover:scale-110 transition-transform"} />
                    <span>{t("install.android_cta")}</span>
                </button>

                <button
                    type="button"
                    onClick={() => setShowIOS(true)}
                    data-testid="install-ios-btn"
                    className={`group inline-flex items-center justify-center gap-2.5 ${padX} ${padY} rounded-sm ${fontSize} font-semibold tracking-[0.3em] uppercase transition-all duration-300 bg-black/40 backdrop-blur border border-gold/40 text-gold hover:bg-gold/10 hover:border-gold hover:-translate-y-0.5 hover:shadow-[0_0_25px_rgba(212,175,55,0.2)]`}
                >
                    <Apple size={14} strokeWidth={1.8} className="group-hover:scale-110 transition-transform" />
                    <span>{t("install.ios_cta")}</span>
                </button>
            </div>

            {showIOS && (
                <div
                    className="fixed inset-0 z-[90] bg-black/85 flex items-center justify-center p-4 animate-fade-up"
                    onClick={() => setShowIOS(false)}
                    data-testid="install-ios-modal"
                >
                    <div
                        className="bg-ink-surface border border-gold/30 rounded-sm w-full max-w-md p-6 sm:p-8 relative shadow-[0_0_60px_rgba(0,0,0,0.6)]"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setShowIOS(false)}
                            className="absolute right-3 top-3 text-gray-400 hover:text-white transition-colors"
                            data-testid="install-ios-close"
                            aria-label={t("install.close")}
                        >
                            <X size={18} />
                        </button>

                        <div className="flex items-center gap-3 mb-6">
                            <div className="w-12 h-12 rounded-sm bg-gold/15 border border-gold/30 flex items-center justify-center">
                                <Apple size={22} className="text-gold" strokeWidth={1.6} />
                            </div>
                            <div>
                                <div className="text-[10px] tracking-[0.3em] uppercase text-gold">{t("install.ios_section")}</div>
                                <h3 className="font-serif text-2xl text-white mt-0.5">{t("install.ios_title")}</h3>
                            </div>
                        </div>

                        <ol className="space-y-4 text-sm text-gray-300 leading-relaxed">
                            <li className="flex gap-3">
                                <span className="shrink-0 w-7 h-7 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs font-bold flex items-center justify-center">1</span>
                                <span>{t("install.ios_step1_a")} <Share size={14} className="inline mx-1 -mt-0.5 text-gold" strokeWidth={1.8} /> {t("install.ios_step1_b")}</span>
                            </li>
                            <li className="flex gap-3">
                                <span className="shrink-0 w-7 h-7 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs font-bold flex items-center justify-center">2</span>
                                <span>{t("install.ios_step2_a")} <Plus size={14} className="inline mx-1 -mt-0.5 text-gold" strokeWidth={1.8} /> <strong className="text-white">{t("install.ios_step2_b")}</strong></span>
                            </li>
                            <li className="flex gap-3">
                                <span className="shrink-0 w-7 h-7 rounded-full bg-gold/15 border border-gold/30 text-gold text-xs font-bold flex items-center justify-center">3</span>
                                <span>{t("install.ios_step3")}</span>
                            </li>
                        </ol>

                        {!isIOS && (
                            <p className="text-[11px] text-gray-500 mt-6 italic" data-testid="install-ios-hint-non-ios">
                                {t("install.ios_hint_open_on_device")}
                            </p>
                        )}

                        <button
                            onClick={() => setShowIOS(false)}
                            className="mt-7 w-full bg-gold text-black font-semibold py-3 rounded-sm hover:bg-gold-light tracking-[0.3em] uppercase text-xs transition-colors"
                            data-testid="install-ios-got-it"
                        >
                            {t("install.ios_got_it")}
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
